import { DataTable } from '@/components/table/DataTable';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Multiselect } from '@/components/ui/multiselect';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/classUtils';
import { ChevronDown } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { literatureReviewPaths } from '@/paths';
import {
  useCreateCriteriaService,
  useDeleteCriteriaService,
  useExecuteAbstractScreeningService,
  useExportLiteratureReviewService,
  useGenerateCriteriaService,
  useGetAbstractScreeningByIdService,
  useGetAbstractScreeningResultsByIdPollingService,
  useGetFullTextScreeningService,
  useUpdateCriteriaService,
  useUpsertFullTextScreeningService,
} from '@/services/literatureReviews';
import { SearchBar } from '@/shared/SearchBar';
import { GetAbstractScreeningResultsResponse } from '@ditto/shared';
import { getPaginationRowModel } from '@tanstack/react-table';
import {
  ArrowRight,
  Download,
  ListChecks,
  ListTree,
  Play,
  Stars,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ScreeningActionBar } from '../components/ScreeningActionBar';
import { CriteriaCard } from './components/CriteriaCard';
import { CriteriaModal } from './components/CriteriaModal';
import { DetailedModal } from './components/DetailedModal';
import { FullTextScreeningWarningModal } from './components/FullTextScreeningWarningModal';
import { useColumns } from './components/Table/columns';
import { WarningModal } from './components/WarningModal';
import { Criteria } from './types';

export const LiteratureReviewAbstractScreeningPage: FC = () => {
  const { literatureReview_id } = useParams();
  const { abstractScreening } =
    useGetAbstractScreeningByIdService(literatureReview_id);
  const { fullTextScreening } =
    useGetFullTextScreeningService(literatureReview_id);

  const { executeAbstractScreening, isExecuteAbstractScreeningPending } =
    useExecuteAbstractScreeningService();

  const { exportLiteratureReview } =
    useExportLiteratureReviewService(literatureReview_id);

  const { generateCriteria, isGenerating: isGenerateCriteriaPending } =
    useGenerateCriteriaService();
  const { createCriteria } = useCreateCriteriaService();
  const { updateCriteria } = useUpdateCriteriaService();
  const { deleteCriteria } = useDeleteCriteriaService();

  const { upsertFullTextScreening, isUpsertFullTextScreeningPending } =
    useUpsertFullTextScreeningService();

  const navigate = useNavigate();

  const handleExportLiteratureReview = (format: 'xlsx' | 'csv') => () => {
    exportLiteratureReview(format);
  };

  const [selectedPaperScreening, setSelectedPaperScreening] = useState<
    GetAbstractScreeningResultsResponse['data'][number] | undefined
  >(undefined);

  const handleStatusChange = (selected: string[]) => {
    setStatuses(
      selected.filter(
        status =>
          status === 'include' ||
          status === 'exclude' ||
          status === 'inconclusive'
      )
    );
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleValidationStatusChange = (selected: string[]) => {
    setValidationStatuses(
      selected.filter(
        status => status === 'not_validated' || status === 'validated'
      )
    );
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [validationStatuses, setValidationStatuses] = useState<string[]>([]);
  const debouncedSearch = useDebounce(search, 300);

  const [isPromptWarningModalOpen, setIsPromptWarningModalOpen] =
    useState(false);

  const [isRunScreeningWarningModalOpen, setIsRunScreeningWarningModalOpen] =
    useState(false);

  const [
    isFullTextScreeningWarningModalOpen,
    setIsFullTextScreeningWarningModalOpen,
  ] = useState(false);

  const filters = {
    ...(debouncedSearch.length > 0 && {
      searchString: debouncedSearch,
    }),
    ...(statuses.length > 0 && {
      inclusionStatus: statuses.join(','),
    }),
    ...(validationStatuses.length > 0 && {
      validationStatus: validationStatuses.join(','),
    }),
  };

  const hasFilters =
    debouncedSearch.length > 0 ||
    statuses.length > 0 ||
    validationStatuses.length > 0;

  const {
    paperScreeningResults,
    resultsStats,
    totalCounts,
    isPending,
    isAbstractScreeningGenerating,
  } = useGetAbstractScreeningResultsByIdPollingService(
    literatureReview_id,
    hasFilters ? filters : undefined
  );

  const statusOptionsWithCounts = useMemo(() => {
    if (!totalCounts) {
      return [
        { label: 'Include', value: 'include' },
        { label: 'Exclude', value: 'exclude' },
        { label: 'Inconclusive', value: 'inconclusive' },
      ];
    }

    return [
      { label: `Include (${totalCounts.include})`, value: 'include' },
      { label: `Exclude (${totalCounts.exclude})`, value: 'exclude' },
      {
        label: `Inconclusive (${totalCounts.inconclusive})`,
        value: 'inconclusive',
      },
    ];
  }, [totalCounts]);

  const validationStatusOptionsWithCounts = useMemo(() => {
    if (!totalCounts) {
      return [
        { label: 'Not validated', value: 'not_validated' },
        { label: 'Validated', value: 'validated' },
      ];
    }

    return [
      {
        label: `Not validated (${totalCounts.not_validated})`,
        value: 'not_validated',
      },
      { label: `Validated (${totalCounts.validated})`, value: 'validated' },
    ];
  }, [totalCounts]);

  const [criteria, setCriteria] = useState<Criteria[]>(
    abstractScreening?.criteria ?? []
  );

  const columns = useColumns(
    literatureReview_id,
    setSelectedPaperScreening,
    paperScreeningResults
  );

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const selectedRowsCount = Object.values(rowSelection).filter(Boolean).length;
  const displayActionBar = selectedRowsCount > 0;

  const clearRowSelection = () => {
    setRowSelection({});
  };

  const validatableRows = paperScreeningResults.filter(
    result =>
      (result.inclusionStatus === 'include' ||
        result.inclusionStatus === 'exclude') &&
      rowSelection[result.id]
  );

  const displayInconclusiveWarning = validatableRows.length < selectedRowsCount;

  const handleEditCriteria = (
    id: string,
    name: string,
    instructions: string
  ) => {
    const selectedCriteria = criteria.find(criterion => criterion.id === id);
    if (!selectedCriteria) {
      return;
    }
    const order = selectedCriteria.order;
    updateCriteria(
      {
        criteriaId: id,
        data: { name, instructions, order },
      },
      {
        onError: () => {
          toast.error('Failed to update criteria');
          setCriteria(
            criteria.map(criterion =>
              criterion.id === id ? selectedCriteria : criterion
            )
          );
        },
      }
    );
    setCriteria(
      criteria.map(criterion =>
        criterion.id === id
          ? { ...criterion, name, instructions, order }
          : criterion
      )
    );
  };
  const handleDeleteCriteria = (id: string) => {
    const selectedCriteria = criteria.find(criterion => criterion.id === id);
    if (!selectedCriteria) {
      return;
    }
    deleteCriteria(
      { criteriaId: id },
      {
        onError: () => {
          toast.error('Failed to delete the criteria');
          setCriteria(
            [...criteria, selectedCriteria].sort((a, b) => a.order - b.order)
          );
        },
      }
    );
    setCriteria(criteria.filter(criterion => criterion.id !== id));
  };

  const handleCreateCriteria = (name: string, instructions: string) => {
    const order =
      criteria.length > 0
        ? Math.max(...criteria.map(criterion => criterion.order)) + 1
        : 0;
    const screeningId = abstractScreening?.id;
    if (!screeningId) {
      return;
    }

    createCriteria(
      {
        name,
        instructions,
        order,
        screeningId,
      },
      {
        onSuccess: data => {
          const newCriteria = {
            id: data.id,
            name: data.name,
            instructions: data.instructions,
            order: data.order,
          };

          setCriteria([...criteria, newCriteria]);
        },
        onError: () => {
          toast.error('Failed to create the criteria');
        },
      }
    );
  };

  const handleExecuteAbstractScreening = () => {
    if (!literatureReview_id) {
      return;
    }
    if (displayTable) {
      setIsRunScreeningWarningModalOpen(true);
      return;
    }
    setAccordionValue(undefined);
    executeAbstractScreening(
      {
        literatureReviewId: literatureReview_id,
        data: {
          criteriaIds:
            abstractScreening?.criteria.map(criterion => criterion.id) ?? [],
        },
      },
      {
        onError: () => {
          toast.error('Failed to execute abstract screening');
        },
      }
    );
  };

  const handleGenerateCriteria = () => {
    if (!literatureReview_id || !criteriaGenerationPrompt.trim()) {
      return;
    }
    if (displayPromptWarning) {
      setIsPromptWarningModalOpen(true);
      return;
    }
    generateCriteria(
      {
        literatureReviewId: literatureReview_id,
        data: {
          prompt: criteriaGenerationPrompt,
        },
      },
      {
        onSuccess: () => {
          toast.success('Criteria generation completed');
          setAccordionValue('criteria');
        },
        onError: () => {
          toast.error('Failed to generate criteria');
        },
      }
    );
  };

  const handlePromptWarningConfirm = () => {
    if (!literatureReview_id || !criteriaGenerationPrompt.trim()) {
      return;
    }
    generateCriteria(
      {
        literatureReviewId: literatureReview_id,
        data: {
          prompt: criteriaGenerationPrompt,
        },
      },
      {
        onSuccess: () => {
          toast.success('Criteria generation completed');
          setAccordionValue('criteria');
          setIsPromptWarningModalOpen(false);
        },
        onError: () => {
          toast.error('Failed to generate criteria');
        },
      }
    );
  };

  const handleRunScreeningWarningConfirm = () => {
    if (!literatureReview_id) {
      return;
    }
    setAccordionValue('');
    executeAbstractScreening(
      {
        literatureReviewId: literatureReview_id,
        data: {
          criteriaIds:
            abstractScreening?.criteria.map(criterion => criterion.id) ?? [],
        },
      },
      {
        onSuccess: () => {
          setIsRunScreeningWarningModalOpen(false);
        },
        onError: () => {
          toast.error('Failed to execute abstract screening');
        },
      }
    );
  };

  const displayPromptWarning = criteria.length > 0;

  const displayTable =
    paperScreeningResults.some(result => result.criteriaResults.length > 0) ||
    isPending ||
    hasFilters;

  const isExecutingAbstractScreening =
    isExecuteAbstractScreeningPending ||
    isAbstractScreeningGenerating ||
    isPending;

  const displayFooter = displayTable && resultsStats;

  const isProceedToAnalysisDisabled =
    !resultsStats || resultsStats?.toValidate > 0;

  const handleUpsertFullTextScreening = () => {
    if (!literatureReview_id) {
      return;
    }
    if (fullTextScreening?.id) {
      setIsFullTextScreeningWarningModalOpen(true);
      return;
    }
    upsertFullTextScreening(
      { literatureReviewId: literatureReview_id },
      {
        onSuccess: () => {
          navigate(`../${literatureReviewPaths.steps.fullTextScreening}`);
        },
        onError: () => {
          toast.error('Failed to upsert full text screening');
        },
      }
    );
  };

  const handleFullTextScreeningWarningConfirm = () => {
    if (!literatureReview_id) {
      return;
    }
    upsertFullTextScreening(
      { literatureReviewId: literatureReview_id },
      {
        onSuccess: () => {
          setIsFullTextScreeningWarningModalOpen(false);
          navigate(`../${literatureReviewPaths.steps.fullTextScreening}`);
        },
        onError: () => {
          toast.error('Failed to upsert full text screening');
        },
      }
    );
  };

  useEffect(() => {
    setCriteria(abstractScreening?.criteria ?? []);
    setCriteriaGenerationPrompt(
      abstractScreening?.criteriaGenerationPrompt ?? ''
    );
  }, [abstractScreening]);

  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    'generateCriteria'
  );

  const [criteriaGenerationPrompt, setCriteriaGenerationPrompt] = useState('');
  const handleSelectNext = useCallback(() => {
    const nextIndex =
      paperScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) + 1;
    if (nextIndex >= paperScreeningResults.length) {
      return;
    }
    setSelectedPaperScreening(paperScreeningResults[nextIndex]);
  }, [paperScreeningResults, selectedPaperScreening]);

  const handleSelectPrevious = useCallback(() => {
    const previousIndex =
      paperScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) - 1;
    if (previousIndex < 0) {
      return;
    }
    setSelectedPaperScreening(paperScreeningResults[previousIndex]);
  }, [paperScreeningResults, selectedPaperScreening]);

  const isFirst = useMemo(() => {
    return (
      paperScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) === 0
    );
  }, [paperScreeningResults, selectedPaperScreening]);
  const isLast = useMemo(() => {
    return (
      paperScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) ===
      paperScreeningResults.length - 1
    );
  }, [paperScreeningResults, selectedPaperScreening]);

  return (
    <FlexCol pt="6" gap="6" h="full">
      <FlexCol px="6" gap="6" h="full" className="overflow-y-auto">
        <Text variant="header1">Abstract Screening</Text>
        <Accordion
          type="single"
          collapsible
          variant="secondary"
          value={accordionValue}
          onValueChange={setAccordionValue}
        >
          <AccordionItem value="generateCriteria">
            <AccordionTrigger className="items-center group hover:no-underline">
              <FlexCol gap="1">
                <FlexRow alignItems="center" gap="2">
                  <ListTree className="w-4 h-4" />
                  <Text className="group-hover:underline">
                    Which papers would you like to exclude?
                  </Text>
                </FlexRow>
                <Text variant="body-sm" color="secondary">
                  Describe which papers to exclude in plain language. The AI
                  will generate structured exclusion criteria for you to review
                  and refine before screening begins.
                </Text>
              </FlexCol>
            </AccordionTrigger>
            <AccordionContent>
              <FlexCol className="gap-4 items-center w-full">
                {displayPromptWarning && (
                  <FlexRow w="full" justifyContent="start">
                    <FlexRow
                      alignItems="center"
                      br="sm"
                      className="border border-orange-100 bg-orange-50"
                      gap="2"
                      px="2"
                      py="1.5"
                    >
                      <TriangleAlert
                        className="text-orange-500"
                        width="16"
                        height="16"
                      />
                      <Text variant="body-sm" color="primary">
                        Changes require re-screening all papers and will
                        permanently delete validated decisions. Export results
                        first to preserve your work.
                      </Text>
                    </FlexRow>
                  </FlexRow>
                )}
                <FlexRow className="w-full">
                  <Textarea
                    value={criteriaGenerationPrompt}
                    onChange={e => setCriteriaGenerationPrompt(e.target.value)}
                    placeholder="e.g. Exclude review articles, editorials, conference abstracts, non-peer-reviewed publications, animal studies, and studies published before 2015."
                    rows={4}
                    className="bg-white w-full focus:ring-0 focus:ring-offset-0 focus:border-transparent focus:outline-none"
                  />
                </FlexRow>
                <Button
                  className="w-fit"
                  disabled={!criteriaGenerationPrompt}
                  loading={isGenerateCriteriaPending}
                  onClick={handleGenerateCriteria}
                >
                  <Stars />
                  Generate Exclusion Criteria
                </Button>
              </FlexCol>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="criteria">
            <AccordionTrigger className="items-center group hover:no-underline">
              <FlexCol gap="1">
                <FlexRow alignItems="center" gap="2">
                  <ListChecks className="w-4 h-4" />
                  <Text className="group-hover:underline">
                    Exclusion Criteria
                  </Text>
                  {criteria.length > 0 && (
                    <Box
                      px="2"
                      br="xxl"
                      className="border border-blue-100 bg-blue-50 text-blue-500"
                    >
                      {criteria.length} criteria configured
                    </Box>
                  )}
                </FlexRow>
                <Text variant="body-sm" color="secondary">
                  Each criterion is evaluated as a yes/no question. Papers with
                  'Yes' for any required criterion are marked as ‘Excluded’
                  after abstract screening.
                </Text>
              </FlexCol>
            </AccordionTrigger>
            <AccordionContent>
              <FlexCol gap="2">
                {displayTable && (
                  <FlexRow w="full" justifyContent="start">
                    <FlexRow
                      alignItems="center"
                      br="sm"
                      className="border border-orange-100 bg-orange-50"
                      gap="2"
                      px="2"
                      py="1.5"
                    >
                      <TriangleAlert
                        className="text-orange-500"
                        width="16"
                        height="16"
                      />
                      <Text variant="body-sm" color="primary">
                        Changes require re-screening all papers and will
                        permanently delete validated decisions. Export results
                        first to preserve your work.
                      </Text>
                    </FlexRow>
                  </FlexRow>
                )}
                {criteria.map(criteria => (
                  <CriteriaCard
                    key={criteria.id}
                    criteria={criteria}
                    onEdit={handleEditCriteria}
                    onDelete={handleDeleteCriteria}
                  />
                ))}
                <CriteriaModal mode="create" onCreate={handleCreateCriteria} />
                <FlexRow w="full" justifyContent="center">
                  <Button
                    className="w-fit"
                    loading={isExecutingAbstractScreening}
                    onClick={handleExecuteAbstractScreening}
                  >
                    <Play />
                    Run abstract screening
                  </Button>
                </FlexRow>
              </FlexCol>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {displayTable && (
          <FlexCol gap="2">
            <FlexRow alignItems="center" justifyContent="between">
              <FlexRow gap="2" alignItems="center">
                <SearchBar
                  searchString={search}
                  setSearchString={setSearch}
                  placeholder="Search"
                />
                <Multiselect
                  options={statusOptionsWithCounts}
                  selected={statuses}
                  onChange={handleStatusChange}
                  placeholder="Screening Decision"
                  className="w-fit text-primary"
                  trigger={
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        'justify-between font-normal w-fit text-primary',
                        statuses.length === 0 && 'text-muted-foreground'
                      )}
                    >
                      <span className="truncate">
                        {statuses.length === 0
                          ? 'Screening Decision'
                          : statuses.length === 1
                            ? statusOptionsWithCounts.find(
                                opt => opt.value === statuses[0]
                              )?.label || statuses[0]
                            : `${statuses.length} selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
                <Multiselect
                  options={validationStatusOptionsWithCounts}
                  selected={validationStatuses}
                  onChange={handleValidationStatusChange}
                  placeholder="Validation Status"
                  className="w-fit text-primary"
                  trigger={
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        'justify-between font-normal w-fit text-primary',
                        validationStatuses.length === 0 &&
                          'text-muted-foreground'
                      )}
                    >
                      <span className="truncate">
                        {validationStatuses.length === 0
                          ? 'Validation Status'
                          : validationStatuses.length === 1
                            ? validationStatusOptionsWithCounts.find(
                                opt => opt.value === validationStatuses[0]
                              )?.label || validationStatuses[0]
                            : `${validationStatuses.length} selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
              </FlexRow>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary">
                    <Download size={16} />
                    Export Screening Results
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={handleExportLiteratureReview('xlsx')}
                  >
                    Export XLSX file
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleExportLiteratureReview('csv')}
                  >
                    Export CSV file
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </FlexRow>
            <DataTable
              columns={columns}
              data={paperScreeningResults}
              isLoading={isPending}
              options={{
                getPaginationRowModel: getPaginationRowModel(),
                getRowId: row => row.id,
                onPaginationChange: setPagination,
                onRowSelectionChange: setRowSelection,
                autoResetPageIndex: false,
                state: {
                  pagination,
                  rowSelection,
                },
              }}
              displayTopPagination={true}
            />
            <ScreeningActionBar
              open={displayActionBar}
              clearSelection={clearRowSelection}
              validatableRows={validatableRows}
              selectedRowsCount={selectedRowsCount}
              displayInconclusiveWarning={displayInconclusiveWarning}
            />
          </FlexCol>
        )}
        {!displayTable && (
          <FlexCol className="w-full h-full pt-20">
            <FlexCol className="items-center justify-center gap-3">
              <ListChecks className="h-6 w-6" />
              <FlexCol className="items-center justify-center gap-1">
                <Text className="text-base font-medium">
                  No abstract screened yet
                </Text>
                <Text className="text-sm text-neutral-500">
                  Define exclusion criteria above
                </Text>
                <Text className="text-sm text-neutral-500">
                  to start begin screening.
                </Text>
              </FlexCol>
            </FlexCol>
          </FlexCol>
        )}
      </FlexCol>
      {displayFooter && (
        <FlexCol
          gap="3"
          py="3"
          alignItems="center"
          justifyContent="center"
          className="sticky bottom-0 border-t border-neutral-200 bg-white"
        >
          <FlexRow gap="2.5" alignItems="center" justifyContent="center">
            <Box br="lg" px="2" className="border border-green-100 bg-green-50">
              <Text variant="body-sm" color="primary">
                {resultsStats.included} included
              </Text>
            </Box>
            <Box br="lg" px="2" className="border border-red-100 bg-red-50">
              <Text variant="body-sm" color="primary">
                {resultsStats.excluded} excluded
              </Text>
            </Box>
            <Box
              br="lg"
              px="2"
              className="border border-neutral-100 bg-neutral-50"
            >
              <Text variant="body-sm" color="primary">
                {resultsStats.toValidate} to validate before moving to the next
                step
              </Text>
            </Box>
          </FlexRow>
          <Button
            onClick={handleUpsertFullTextScreening}
            loading={isUpsertFullTextScreeningPending}
            disabled={isProceedToAnalysisDisabled}
            className="w-fit"
          >
            Proceed to analysis
            <ArrowRight />
          </Button>
        </FlexCol>
      )}
      <DetailedModal
        key={selectedPaperScreening?.id}
        paperScreening={selectedPaperScreening}
        isOpen={!!selectedPaperScreening}
        onOpenChange={() => setSelectedPaperScreening(undefined)}
        selectNext={handleSelectNext}
        selectPrevious={handleSelectPrevious}
        isFirst={isFirst}
        isLast={isLast}
      />
      <WarningModal
        isOpen={isPromptWarningModalOpen}
        onOpenChange={setIsPromptWarningModalOpen}
        isLoading={isGenerateCriteriaPending}
        onConfirm={handlePromptWarningConfirm}
        type="prompt"
      />
      <WarningModal
        isOpen={isRunScreeningWarningModalOpen}
        onOpenChange={setIsRunScreeningWarningModalOpen}
        isLoading={isExecutingAbstractScreening}
        onConfirm={handleRunScreeningWarningConfirm}
        type="run_screening"
      />
      <FullTextScreeningWarningModal
        isOpen={isFullTextScreeningWarningModalOpen}
        onOpenChange={setIsFullTextScreeningWarningModalOpen}
        isLoading={isUpsertFullTextScreeningPending}
        onConfirm={handleFullTextScreeningWarningConfirm}
      />
    </FlexCol>
  );
};
